import React, { useState, useEffect } from 'react';
import { LoadingSpinner } from './common/Loading';
import CourseSelect from './courseCompare/CourseSelect';
import { useCourses } from '../hooks/useCourse';
import { getSectionConfig, SECTION_LABELS } from '../utils/courseUtils';
import '../styles/course-compare.css';

const CourseComparison = () => {
  const [selectedCourses, setSelectedCourses] = useState([null, null, null]);
  const [activeSection, setActiveSection] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 991);
  const { courses, loading, error } = useCourses();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 991);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleCourseSelect = (selectedOption, index) => {
    const newSelectedCourses = [...selectedCourses];
    newSelectedCourses[index] = selectedOption;
    setSelectedCourses(newSelectedCourses);
  };

  const isLastActive = (index) => {
    return (
      selectedCourses[index] !== null &&
      selectedCourses.slice(index + 1).every((course) => course === null)
    );
  };

  const getAvailableOptions = (index) => {
    return courses.filter(
      (course) =>
        !selectedCourses.some(
          (selected, i) =>
            i !== index && selected && selected.value === course.value,
        ),
    );
  };

  const renderMobileView = () => {
    const firstSelectedCourse = selectedCourses.find(
      (course) => course !== null,
    );
    const sections = firstSelectedCourse
      ? getSectionConfig(firstSelectedCourse).filter(
          (section) => section.label !== 'Course',
        )
      : [];

    return (
      <div className="mobile-comparison">
        <div className="course-selectors">
          {selectedCourses.map((course, index) => (
            <div key={index} className="course-select-wrapper">
              <CourseSelect
                value={selectedCourses[index]}
                onChange={(option) => handleCourseSelect(option, index)}
                options={getAvailableOptions(index)}
                isDisabled={index > 0 && !selectedCourses[index - 1]}
                isClearable={isLastActive(index)}
                index={index}
              />
            </div>
          ))}
        </div>

        {sections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="accordion-section">
            <div
              className="accordion-header"
              onClick={() =>
                setActiveSection(
                  activeSection === sectionIndex ? null : sectionIndex,
                )
              }
            >
              <span>{section.label}</span>
              <span>{activeSection === sectionIndex ? '-' : '+'}</span>
            </div>
            {activeSection === sectionIndex && (
              <div className="accordion-content">
                {selectedCourses.map(
                  (course, courseIndex) =>
                    course && (
                      <div key={courseIndex} className="comparison-item">
                        <div className="course-name">{course.label}</div>
                        <div className="course-content">
                          {
                            getSectionConfig(course).filter(
                              (s) => s.label !== 'Course',
                            )[sectionIndex].content
                          }
                        </div>
                      </div>
                    ),
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderDesktopView = () => {
    const sections = selectedCourses[0]
      ? getSectionConfig(selectedCourses[0])
      : [];

    return (
      <div className="comparison-columns">
        {/* Labels Column */}
        <div className="section-labels">
          <div className="xsection-label"></div>
          {SECTION_LABELS.map((label) => (
            <div key={label} className="section-label">
              {label}
            </div>
          ))}
        </div>

        {/* Course Columns */}
        {[0, 1, 2].map((index) => (
          <div key={index} className="comparison-column">
            <div className="course-select-wrapper">
              <CourseSelect
                value={selectedCourses[index]}
                onChange={(option) => handleCourseSelect(option, index)}
                options={getAvailableOptions(index)}
                isDisabled={index > 0 && !selectedCourses[index - 1]}
                isClearable={isLastActive(index)}
                index={index}
              />
            </div>
            {selectedCourses[index]
              ? getSectionConfig(selectedCourses[index]).map((section) => (
                  <div key={section.label} className="comparison-section">
                    {section.content}
                  </div>
                ))
              : isLastActive(index - 1) && (
                  <div className="empty-section">
                    <strong>Select a course to compare</strong>
                  </div>
                )}
          </div>
        ))}
      </div>
    );
  };

  const getUrlParams = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('course');
  };

  useEffect(() => {
    const courseId = getUrlParams();
    if (courseId && courses.length > 0) {
      let selectedCourse = null;
      for (const courseGroup of courses) {
        const found = courseGroup.options.find(
          (option) => option.id === courseId,
        );
        if (found) {
          selectedCourse = found;
          break;
        }
      }
      if (selectedCourse) {
        setSelectedCourses([selectedCourse, null, null]);
      }
    }
  }, [courses]);

  if (loading) return <LoadingSpinner />;
  if (error) return <div>Error loading courses</div>;

  return (
    <div className="course-comparison">
      <div className="comparison-container">
        {isMobile ? renderMobileView() : renderDesktopView()}
      </div>
    </div>
  );
};

export default CourseComparison;
